import { DateTime } from "luxon";

export type DateTimeRange = { start: DateTime; end: DateTime };

export function parseIsoDateTimeRange(rangeStart?: string, rangeEnd?: string): DateTimeRange | null {
  if (!rangeStart || !rangeEnd) {
    return null;
  }

  const start = DateTime.fromISO(rangeStart, { setZone: true });
  const end = DateTime.fromISO(rangeEnd, { setZone: true });

  if (!start.isValid || !end.isValid) {
    return null;
  }

  return { start, end };
}

export function dateTimeRangesOverlap(a: DateTimeRange, b: DateTimeRange): boolean {
  return a.start < b.end && b.start < a.end;
}

/**
 * Formats an ISO 8601 date-time string into a human-readable string.
 * Preserves the local time from the ISO string's timezone offset.
 * Example: '2025-12-15T09:30:00.000-05:00' -> "Monday, December 15 at 9:30 AM"
 */
export function formatDateTimeDisplay(isoString: string, options: { includeYear?: boolean } = {}): string {
  const dt = DateTime.fromISO(isoString, { setZone: true });
  const dateFormat = options.includeYear ? "EEEE, MMMM d, yyyy 'at' h:mm a" : "EEEE, MMMM d 'at' h:mm a";

  return dt.toFormat(dateFormat);
}

type FormatDateDisplayResult = { date: string; formatted: string } | { date: string; error: string };
export function formatDateDisplay(date: string): FormatDateDisplayResult {
  const dt = DateTime.fromISO(date);

  if (!dt.isValid) {
    return { date, error: `Invalid date "${date}". Use YYYY-MM-DD format.` };
  }

  return { date, formatted: dt.toFormat("EEEE, MMMM d, yyyy") };
}

export type LeapYearDateKind = "start" | "end";

/**
 * Adjusts February 29 dates to valid dates for systems that don't support leap day.
 * Start dates are moved to February 28, end dates are moved to March 1.
 * Date format: yyyy-mm-dd or mm/dd/yyyy
 */
export function adjustLeapYearDate(date: string, kind: LeapYearDateKind): string {
  if (date.includes("-")) {
    if (!/^\d{4}-02-29$/.test(date)) {
      return date;
    }

    const year = date.slice(0, 4);

    return kind === "start" ? `${year}-02-28` : `${year}-03-01`;
  }

  if (!date.startsWith("02/29/")) {
    return date;
  }

  return kind === "start" ? `02/28/${date.slice(6)}` : `03/01/${date.slice(6)}`;
}

export function getDurationMinutes(start: Date, end: Date): number {
  const durationMs = end.getTime() - start.getTime();

  return Math.max(Math.round(durationMs / 60000), 0);
}

export function isValidTimestamp(value: string): value is string {
  return !Number.isNaN(Date.parse(value));
}

// Formats a date-of-birth may arrive in. The collect tool asks the model for
// mm/dd/yyyy, but the model doesn't always zero-pad or keep the separator stable
// across turns ("8/9/1990" vs "08/09/1990"), and it occasionally hands back ISO.
// `M`/`d` accept 1–2 digits, so the slash form covers both padded and unpadded.
const DOB_INPUT_FORMATS = ["M/d/yyyy", "M-d-yyyy", "yyyy-MM-dd"];

/**
 * Canonicalizes a collected date of birth to ISO `yyyy-MM-dd`, or returns null if
 * it can't be parsed. Use it to compare two DOB captures for the same calendar
 * date without being fooled by formatting drift (padding/separators) between turns.
 */
export function normalizeDateOfBirth(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();

  for (const format of DOB_INPUT_FORMATS) {
    const dt = DateTime.fromFormat(trimmed, format);

    if (dt.isValid) {
      return dt.toFormat("yyyy-MM-dd");
    }
  }

  return null;
}
